import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock3, Crosshair, Fish, MapPin, ShieldCheck } from "lucide-react";
import { Button } from "../components/Button";
import { Field } from "../components/Field";
import { Input } from "../components/Input";
import { preferredRodCount, rememberRodCount } from "../components/RodCountPicker";
import {
  cacheOfflineLakes,
  cacheServerSessions,
  createOfflineSession,
  listOfflineSessions,
  readCachedServerSessions,
  readOfflineLakes,
  useOnlineStatus,
} from "../lib/offline-session";

function pad(value: number) { return String(value).padStart(2, "0"); }
function localDateTime(date: Date) { return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`; }
function normaliseLocal(raw: string | null, fallback: Date) {
  if (!raw) return localDateTime(fallback);
  const match = raw.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
  return match ? match[1] : localDateTime(fallback);
}

const DURATIONS = [6, 12, 24, 48] as const;

export default function StartSessionSimplePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const online = useOnlineStatus();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const advisorId = params.get("advisorId");
  const lakeId = params.get("lake_id") ?? "";
  const peg = params.get("peg") ?? "";
  const now = useMemo(() => new Date(), []);

  const [form, setForm] = useState({
    start_at: normaliseLocal(params.get("start_at"), now),
    rods_count: params.get("rods_count") ?? String(preferredRodCount()),
    target_species: params.get("target_species") === "carp" || params.get("target_species") === "catfish" ? String(params.get("target_species")) : "both",
    notes: "",
  });
  const [durationHours, setDurationHours] = useState(24);
  const [customFinish, setCustomFinish] = useState("");
  const [privacy, setPrivacy] = useState("Contribute anonymously to CarpIntel statistics");
  const [sharePeg, setSharePeg] = useState(false);
  const [gps, setGps] = useState<{ lat: number; lng: number } | null>(null);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const lakesQuery = useQuery({
    queryKey: ["carpintel_lakes"],
    queryFn: async () => {
      try {
        const response = await fetch("/app-api/lakes");
        if (!response.ok) throw new Error("Could not load lakes");
        const rows = await response.json(); cacheOfflineLakes(rows); return rows;
      } catch (error) {
        const cached = readOfflineLakes(); if (cached.length) return cached; throw error;
      }
    },
  });
  const sessionsQuery = useQuery({
    queryKey: ["carpintel_sessions"],
    queryFn: async () => {
      try {
        const response = await fetch("/app-api/sessions");
        if (!response.ok) throw new Error("Could not load sessions");
        const rows = await response.json(); cacheServerSessions(rows); return rows;
      } catch (error) {
        const cached = readCachedServerSessions(); const local = listOfflineSessions();
        if (cached.length || local.length || !navigator.onLine) return [...local, ...cached]; throw error;
      }
    },
  });
  const preferencesQuery = useQuery({
    queryKey: ["catchcompass_preferences"],
    queryFn: () => fetch("/app-api/preferences").then((r) => (r.ok ? r.json() : {})),
    enabled: online,
  });

  useEffect(() => {
    const preferences = preferencesQuery.data;
    if (!preferences) return;
    const preferredDuration = Number(preferences.default_session_duration_hours ?? 24);
    if (preferredDuration >= 1 && preferredDuration <= 168) setDurationHours(preferredDuration);
    if (preferences.default_privacy_setting) setPrivacy(String(preferences.default_privacy_setting));
    setSharePeg(preferences.default_share_peg_anonymously === true);
  }, [preferencesQuery.data]);

  const lakes: any[] = lakesQuery.data ?? [];
  const selectedLake = lakes.find((lake) => String(lake.id) === String(lakeId));
  const activeSession = (sessionsQuery.data ?? []).find((session: any) => !session.end_at) ?? listOfflineSessions().find((session) => !session.end_at);
  const isCustom = !DURATIONS.includes(durationHours as any);

  function plannedFinish() {
    if (isCustom && customFinish) return new Date(customFinish);
    return new Date(new Date(form.start_at).getTime() + durationHours * 60 * 60 * 1000);
  }

  function captureLocation() {
    if (!navigator.geolocation) { setMessage("Location is not available on this device."); return; }
    setGpsBusy(true); setMessage("");
    navigator.geolocation.getCurrentPosition((position) => {
      setGps({ lat: position.coords.latitude, lng: position.coords.longitude }); setGpsBusy(false);
    }, () => { setGpsBusy(false); setMessage("Could not capture your location. It is optional, so you can continue without it."); }, { enableHighAccuracy: true, timeout: 12000 });
  }

  async function save(event: React.FormEvent) {
    event.preventDefault(); setMessage("");
    if (!lakeId || !peg) { setMessage("Choose the water and peg through Start Fishing first."); return; }
    const start = new Date(form.start_at);
    const planned = plannedFinish();
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(planned.getTime()) || planned.getTime() <= start.getTime()) { setMessage("Choose a planned finish after the session start."); return; }

    const payload = {
      lake_id: Number(lakeId), peg, start_at: start.toISOString(), end_at: null, planned_end_at: planned.toISOString(),
      rods_count: Math.max(1, Number(form.rods_count || 1)), target_species: form.target_species,
      privacy_setting: privacy, share_peg_anonymously: sharePeg, notes: form.notes.trim() || "", advisor_recommendation_id: advisorId || null,
      weather: "", rain_observed: "not-recorded", pressure_trend: "not-recorded", air_temp_c: null, wind_direction: "", wind_speed_mph: null,
      air_pressure_hpa: null, cloud_cover_pct: null, water_temp_c: null,
      gps_latitude: gps?.lat ?? null, gps_longitude: gps?.lng ?? null,
    };
    const startOffline = () => {
      const local = createOfflineSession(payload, selectedLake?.name || "Saved lake");
      rememberRodCount(Number(payload.rods_count)); setSaving(false); navigate(`/sessions/${local.id}`);
    };

    setSaving(true);
    if (!online) { startOffline(); return; }
    try {
      const response = await fetch("/app-api/sessions", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const result = await response.json();
      if (!response.ok || !result.id) throw new Error(result.error || "Could not start session");
      rememberRodCount(Number(payload.rods_count));
      await fetch("/app-api/preferences", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ default_privacy_setting: privacy, default_share_peg_anonymously: sharePeg, default_session_duration_hours: Math.max(1, Math.min(168, Math.round((planned.getTime() - start.getTime()) / 3600000))) }) }).catch(() => null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["carpintel_sessions"] }),
        queryClient.invalidateQueries({ queryKey: ["catchcompass_preferences"] }),
      ]);
      navigate(`/live/${result.id}`);
    } catch (error) {
      if (!navigator.onLine) { startOffline(); return; }
      setMessage(error instanceof Error ? error.message : "Could not start session"); setSaving(false);
    }
  }

  if (!lakeId || !peg) return <Navigate to="/scout" replace />;
  if (lakesQuery.isLoading || sessionsQuery.isLoading) return <div className="mx-auto max-w-[760px] px-4 py-8 text-sm text-secondary">Loading your plan…</div>;
  if (activeSession) return <div className="mx-auto max-w-[760px] space-y-4 px-4 py-8 pb-28"><h1 className="text-2xl font-semibold text-primary">A session is already live</h1><p className="text-sm leading-6 text-secondary">CatchCompass keeps one live session at a time so exposure evidence stays clean.</p><Button asChild><Link to={String(activeSession.id).startsWith("offline-") ? `/sessions/${activeSession.id}` : `/live/${activeSession.id}`}>Resume current session</Link></Button></div>;

  return (
    <div className="mx-auto max-w-[760px] space-y-6 px-4 py-6 pb-28">
      <header><Link to="/scout" className="text-sm font-medium text-accent">← Peg</Link><p className="mt-3 text-xs font-semibold uppercase tracking-wide text-accent">3 · Plan</p><h1 className="mt-1 text-2xl font-semibold text-primary">Ready to fish?</h1><p className="mt-1 text-sm leading-6 text-secondary">Only the essentials. Weather is captured automatically. Rig, bait and rod positions belong to the live rods, where they can change independently.</p></header>
      {!online ? <div className="rounded-xl border border-accent/40 bg-inset p-3 text-sm text-secondary">No signal is fine. The session can start offline and sync later.</div> : null}

      <form onSubmit={save} className="space-y-5">
        <section className="rounded-xl border border-border bg-raised p-4">
          <div className="flex items-center gap-2"><MapPin className="size-4 text-accent" /><h2 className="font-semibold text-primary">Where</h2></div>
          <p className="mt-3 text-lg font-semibold text-primary">{selectedLake?.name || "Selected water"}</p><p className="mt-1 text-sm text-secondary">{peg}</p>
          <Link to="/scout" className="mt-3 inline-block text-xs font-semibold text-accent">Change water or peg</Link>
        </section>

        <section className="space-y-4 rounded-xl border border-border bg-raised p-4">
          <div className="flex items-center gap-2"><Clock3 className="size-4 text-accent" /><h2 className="font-semibold text-primary">When</h2></div>
          <Field label="Start" id="start-time"><Input id="start-time" type="datetime-local" value={form.start_at} onChange={(e) => setForm((current) => ({ ...current, start_at: e.target.value }))} required /></Field>
          <div><p className="text-sm font-medium text-primary">Expected session length</p><div className="mt-2 grid grid-cols-5 gap-2">{DURATIONS.map((hours) => <button key={hours} type="button" onClick={() => setDurationHours(hours)} className={`rounded-lg border px-2 py-2 text-sm font-semibold ${durationHours === hours ? "border-accent bg-accent text-white" : "border-border bg-background text-primary"}`}>{hours}h</button>)}<button type="button" onClick={() => { setDurationHours(0); if (!customFinish) setCustomFinish(localDateTime(new Date(new Date(form.start_at).getTime() + 24 * 3600000))); }} className={`rounded-lg border px-2 py-2 text-sm font-semibold ${isCustom ? "border-accent bg-accent text-white" : "border-border bg-background text-primary"}`}>Custom</button></div></div>
          {isCustom ? <Field label="Planned finish" id="custom-finish"><Input id="custom-finish" type="datetime-local" value={customFinish} onChange={(e) => setCustomFinish(e.target.value)} required /></Field> : <p className="text-xs text-secondary">Planned finish: {plannedFinish().toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}. CatchCompass warns before an automatic close one hour later if you forget.</p>}
        </section>

        <section className="space-y-4 rounded-xl border border-border bg-raised p-4">
          <div className="flex items-center gap-2"><Fish className="size-4 text-accent" /><h2 className="font-semibold text-primary">Fishing</h2></div>
          <div className="grid grid-cols-2 gap-4"><Field label="Rods" id="start-rods"><Input id="start-rods" type="number" min="1" max="20" value={form.rods_count} onChange={(e) => setForm((current) => ({ ...current, rods_count: e.target.value }))} required /></Field><Field label="Target" id="start-target"><select id="start-target" value={form.target_species} onChange={(e) => setForm((current) => ({ ...current, target_species: e.target.value }))} className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-primary"><option value="carp">Carp</option><option value="catfish">Catfish</option><option value="both">Both</option></select></Field></div>
          <Field label="Notes" id="start-notes"><textarea id="start-notes" value={form.notes} onChange={(e) => setForm((current) => ({ ...current, notes: e.target.value }))} className="min-h-20 w-full rounded-lg border border-border bg-background p-3 text-sm text-primary" placeholder="Optional" /></Field>
        </section>

        <section className="space-y-3 rounded-xl border border-border bg-raised p-4">
          <div className="flex items-center gap-2"><ShieldCheck className="size-4 text-accent" /><h2 className="font-semibold text-primary">Privacy & location</h2></div>
          <p className="text-xs leading-5 text-secondary">These are remembered as your defaults. Exact location is never required for community evidence.</p>
          <select value={privacy} onChange={(e) => setPrivacy(e.target.value)} className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-primary"><option value="Private">Private</option><option value="Contribute anonymously to CarpIntel statistics">Contribute anonymously</option><option value="Shared">Shared</option></select>
          <label className="flex items-center gap-2 text-sm text-secondary"><input type="checkbox" checked={sharePeg} onChange={(e) => setSharePeg(e.target.checked)} /> Allow peg to contribute anonymously</label>
          <Button type="button" variant="secondary" className="w-full" onClick={captureLocation} disabled={gpsBusy}><Crosshair className="size-4" /> {gpsBusy ? "Getting location…" : gps ? "Private GPS captured" : "Use my current location privately"}</Button>
        </section>

        {message ? <p className="rounded-lg border border-error bg-error-weak p-3 text-sm text-error" role="alert">{message}</p> : null}
        <Button type="submit" size="lg" className="w-full" isLoading={saving}>Start fishing</Button>
      </form>
    </div>
  );
}
